import type { ISbStoryData } from "@storyblok/react";
import Head from "next/head";
import { useRouter } from "next/router";

import type { Blok } from "@/bloks/Article";

import { DEFAULT_LOCALE, LOCALES, SITE_ORIGIN } from "./i18n.config.mjs";

// Storyblok stores info-pages and category content under folder prefixes that
// shouldn't appear in public URLs. Strip them, plus any locale prefix and
// trailing slashes.
const sanitizeSlug = (raw: string) => {
  let s = raw.replace(/^[a-z]{2}\//, "");
  s = s.replace("info-pages/", "");
  s = s.replace("category/", "");
  s = s.replace(/\/+$/, "");
  if (s === "home") return "";
  return s;
};

const buildLocaleUrl = (locale: string, slug: string) => {
  const prefix = locale === DEFAULT_LOCALE ? "" : `/${locale}`;
  const path = sanitizeSlug(slug);
  if (path) return `${SITE_ORIGIN}${prefix}/${path}`;
  // Locale homepage: only the EN root keeps a trailing slash; .com/no (no /).
  return prefix === "" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${prefix}`;
};

const Alternates = ({ story }: { story: ISbStoryData<Blok> | null }) => {
  const { locale = DEFAULT_LOCALE } = useRouter();
  if (!story) return null;

  const slugByLocale: Record<string, string> = {};
  slugByLocale[locale] = story.full_slug;
  if (story.translated_slugs) {
    for (const t of story.translated_slugs) {
      if (t.path && LOCALES.includes(t.lang)) {
        slugByLocale[t.lang] = t.path;
      }
    }
  }
  if (!slugByLocale[DEFAULT_LOCALE] && story.default_full_slug) {
    slugByLocale[DEFAULT_LOCALE] = story.default_full_slug;
  }

  const canonicalHref = buildLocaleUrl(locale, slugByLocale[locale]);
  const defaultHref = slugByLocale[DEFAULT_LOCALE]
    ? buildLocaleUrl(DEFAULT_LOCALE, slugByLocale[DEFAULT_LOCALE])
    : null;

  return (
    <Head>
      <link rel="canonical" href={canonicalHref} />
      {LOCALES.map((lng) => {
        const slug = slugByLocale[lng];
        if (!slug) return null;
        return (
          <link
            key={lng}
            rel="alternate"
            hrefLang={lng}
            href={buildLocaleUrl(lng, slug)}
          />
        );
      })}
      {defaultHref && (
        <link rel="alternate" hrefLang="x-default" href={defaultHref} />
      )}
    </Head>
  );
};

export const AlternatesHead = ({
  value,
}: {
  value: Record<string, string>;
}) => {
  const { locale = DEFAULT_LOCALE } = useRouter();

  const toUrl = (lng: string) => {
    const path = value[lng];
    if (!path) return null;
    const prefix = lng === DEFAULT_LOCALE ? "" : `/${lng}`;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const url = `${SITE_ORIGIN}${prefix}${normalized}`;
    // Only the EN root (.com/) keeps a trailing slash; everything else has none
    // to match Next.js's default trailingSlash behaviour.
    if (prefix === "" && normalized === "/") return url;
    return url.replace(/\/+$/, "");
  };

  const canonicalHref = toUrl(locale);
  const defaultHref = toUrl(DEFAULT_LOCALE);

  return (
    <Head>
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      {LOCALES.map((lng) => {
        const href = toUrl(lng);
        if (!href) return null;
        return <link key={lng} rel="alternate" hrefLang={lng} href={href} />;
      })}
      {defaultHref && (
        <link rel="alternate" hrefLang="x-default" href={defaultHref} />
      )}
    </Head>
  );
};

const StoryHead = ({ story }: { story: ISbStoryData<Blok> | null }) => {
  if (!story) return null;
  return <Alternates story={story} />;
};

export const I18nHead = ({ story }: { story: ISbStoryData<Blok> | null }) => {
  return <StoryHead story={story} />;
};
